import { useState, useEffect } from 'react'
import { X, Building2 } from 'lucide-react'
import type { FinancialInstitution, FinancialInstitutionCreate } from '../api/adminApi'

interface Props {
  institution?: FinancialInstitution | null
  onSubmit: (data: FinancialInstitutionCreate) => Promise<void>
  onClose: () => void
}

export default function FinancialInstitutionForm({ institution, onSubmit, onClose }: Props) {
  const isEdit = !!institution
  const [name, setName] = useState(institution?.name ?? '')
  const [code, setCode] = useState(institution?.code ?? '')
  const [logoUrl, setLogoUrl] = useState(institution?.logo_url ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setName(institution?.name ?? '')
    setCode(institution?.code ?? '')
    setLogoUrl(institution?.logo_url ?? '')
    setError('')
  }, [institution])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !code.trim()) {
      setError('Name and Code are required')
      return
    }
    setLoading(true)
    setError('')
    try {
      await onSubmit({ name: name.trim(), code: code.trim().toUpperCase(), logo_url: logoUrl.trim() || undefined })
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit Institution' : 'Add Financial Institution'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Institution Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Westpac Banking Corporation"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              maxLength={255}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. WBC"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono"
              maxLength={50}
              required
            />
            <p className="text-xs text-gray-500 mt-1">Short code used in the system (auto uppercased)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (optional)</label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors font-medium"
            >
              {loading ? 'Saving...' : isEdit ? 'Update' : 'Add Institution'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
